import { useState } from 'react';
import { User, Search, Loader2 } from 'lucide-react';
import type { Friend } from '@omokage/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAllFriends, useAddPlayersToPhoto } from '@/hooks/usePhotoFriends';

/**
 * PlayerSelectDialog - Select players from existing friends list
 *
 * Features:
 * - Search/filter by displayName
 * - Exclude already added players
 * - Add multiple players without closing dialog
 * - Loading state during API calls
 * - Empty state when no candidates
 *
 * Usage:
 * ```tsx
 * <PlayerSelectDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   photoId="photo123"
 *   existingPlayers={photo.friends}
 * />
 * ```
 */
export interface PlayerSelectDialogProps {
  /** Dialog open state */
  open: boolean;
  /** Dialog state change handler */
  onOpenChange: (open: boolean) => void;
  /** Photo ID to add players to */
  photoId: string;
  /** Already added players (to exclude from candidates) */
  existingPlayers: Friend[];
  /** Callback when player is added */
  onPlayerAdded?: (player: Friend) => void;
}

export function PlayerSelectDialog({
  open,
  onOpenChange,
  photoId,
  existingPlayers,
  onPlayerAdded,
}: PlayerSelectDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const allFriends = useAllFriends();
  const addPlayers = useAddPlayersToPhoto();

  // Filter available players (not already in photo, matching search)
  const availablePlayers = allFriends
    .filter((f) => !existingPlayers.some((e) => e.vrchatId === f.vrchatId))
    .filter((f) => f.displayName.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleAddPlayer = async (vrchatId: string) => {
    try {
      const result = await addPlayers.mutateAsync({ photoId, vrchatIds: [vrchatId] });
      // Call callback for each added player
      result.added.forEach((player) => {
        onPlayerAdded?.(player);
      });
      // Don't close dialog - allow multiple additions
    } catch (error) {
      console.error('Failed to add player:', error);
    }
  };

  const handleClose = () => {
    setSearchQuery(''); // Reset search on close
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[70] sm:max-w-md bg-card border-border text-card-foreground">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Add Player</DialogTitle>
        </DialogHeader>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search players..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-muted border-border text-card-foreground placeholder:text-muted-foreground"
            autoFocus
          />
        </div>

        {/* Player List */}
        <div className="custom-scrollbar max-h-[300px] overflow-y-auto space-y-1">
          {availablePlayers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? (
                <p>No players found matching &ldquo;{searchQuery}&rdquo;</p>
              ) : (
                <p>No available players to add</p>
              )}
            </div>
          ) : (
            availablePlayers.map((player) => (
              <button
                key={player.vrchatId}
                onClick={() => handleAddPlayer(player.vrchatId)}
                disabled={addPlayers.isPending}
                className="w-full flex items-center gap-3 p-3 rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
              >
                <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm text-card-foreground truncate flex-1">
                  {player.displayName}
                </span>
                {addPlayers.isPending && (
                  <Loader2 className="w-4 h-4 text-muted-foreground animate-spin flex-shrink-0" />
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <DialogFooter>
          <Button onClick={handleClose} variant="outline" className="w-full sm:w-auto">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
